import { DownloadIcon } from 'lucide-react';
import { Dispatch, SetStateAction, useMemo } from 'react';

import { Widget } from '@@/Widget';
import { TextTip } from '@@/Tip/TextTip';
import { Button } from '@@/buttons';
import { BEFeatureIndicator } from '@@/BEFeatureIndicator';
import { FormControl } from '@@/form-components/FormControl';
import { MultiSelect } from '@@/form-components/PortainerSelect';

import { FeatureId } from '../../feature-flags/enums';
import { DateTimeRangePicker } from '../components/DateTimeRangePicker';

import { FilterState } from './types';

export function FilterBar({
  value,
  setState,
  onExport,
  environments = [],
  users = [],
}: {
  value: FilterState;
  setState: Dispatch<SetStateAction<FilterState>>;
  onExport: () => void;
  environments?: Array<string>;
  users?: Array<string>;
}) {
  const environmentOptions = useOptions(environments);
  const userOptions = useOptions(users);

  return (
    <Widget>
      <Widget.Body>
        <form className="form-horizontal">
          <FormControl label="Date range">
            <div className="max-w-1/2">
              <DateTimeRangePicker
                startDate={value.range.start || undefined}
                endDate={value.range.end || undefined}
                onStartDateChange={(d) =>
                  setState((prev) => ({
                    ...prev,
                    range: { ...prev.range, start: d ?? null },
                  }))
                }
                onEndDateChange={(d) =>
                  setState((prev) => ({
                    ...prev,
                    range: { ...prev.range, end: d ?? null },
                  }))
                }
              />
            </div>
          </FormControl>

          <FormControl label="User">
            <MultiSelect
              options={userOptions}
              value={value.usernames}
              onChange={(selected) =>
                setState((prev) => ({
                  ...prev,
                  usernames: selected || [],
                }))
              }
              data-cy="activity-logs-username-filter"
            />
          </FormControl>

          <FormControl label="Environment">
            <MultiSelect
              options={environmentOptions}
              value={value.contexts}
              onChange={(selected) =>
                setState((prev) => ({
                  ...prev,
                  contexts: selected || [],
                }))
              }
              data-cy="activity-logs-environment-filter"
            />
          </FormControl>

          <TextTip color="blue">
            Portainer user activity logs have a maximum retention of 7 days.
          </TextTip>

          <div className="mt-4">
            <Button
              color="primary"
              icon={DownloadIcon}
              onClick={onExport}
              className="!ml-0"
              data-cy="activity-logs-export-csv-button"
            >
              Export as CSV
            </Button>
            <BEFeatureIndicator featureId={FeatureId.ACTIVITY_AUDIT} />
          </div>
        </form>
      </Widget.Body>
    </Widget>
  );
}

export function useOptions(values: string[] | undefined) {
  return useMemo(
    () => values?.map((value) => ({ label: value, value })) || [],
    [values]
  );
}
